// src/components/NavBar.jsx
// FINAL - ADMIN ONLY: Options + Central Kitchen + Notification Center + Private Media + Open/Import/Export Contacts VCF

import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./NavBar.css";
import { useAuth } from "../context/AuthContext";
import PermGate from "./PermGate";
import PrivateMediaManager from "./PrivateMediaManager";

import {
  API,
  setApiBase,
  setBranch,
  getActiveBranch,
  lockBranch,
  unlockBranch,
  getBranchLock,
  getNotifications,
  getUnreadNotificationCount,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
  createTestNotification,
} from "../apiBase";

import BatchManager from "../BatchManager";
import BulkDeleteModal from "./BulkDeleteModal";
import UserAdminModal from "./UserAdminModal";

function getCookie(name) {
  try {
    const m = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
    return m ? decodeURIComponent(m[2]) : "";
  } catch {
    return "";
  }
}

const BRANCH_LABEL = {
  jounieh: "Jounieh",
  tripoli: "Tripoli",
  batroun: "Batroun",
  zgharta: "Zgharta",
  ehden: "Ehden",
};

function safeLower(v) {
  return String(v || "").trim().toLowerCase();
}

function normalizeUserBranches(raw) {
  const arr = Array.isArray(raw)
    ? raw
    : String(raw || "")
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);

  const cleaned = arr.map(safeLower).filter(Boolean);
  if (cleaned.includes("*")) return Object.keys(BRANCH_LABEL);

  return cleaned.filter(
    (b, i) => Object.prototype.hasOwnProperty.call(BRANCH_LABEL, b) && cleaned.indexOf(b) === i
  );
}

function fmtWhen(ts) {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return ts;
    return d.toLocaleString();
  } catch {
    return ts;
  }
}

function NotificationItem({ item, onRead, onDelete, compact = false }) {
  const typeClass =
    item?.type === "error"
      ? "notif-error"
      : item?.type === "warning"
      ? "notif-warning"
      : item?.type === "success"
      ? "notif-success"
      : "notif-info";

  const unread = !item?.is_read;

  return (
    <div
      className={`notif-item ${typeClass} ${unread ? "unread" : "read"} ${compact ? "compact" : ""}`}
      style={{
        border: "1px solid rgba(255,255,255,.08)",
        borderRadius: 12,
        padding: compact ? "8px 10px" : "10px 12px",
        background: unread ? "rgba(255,255,255,.06)" : "rgba(255,255,255,.03)",
        display: "grid",
        gap: 6,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
        <div style={{ fontSize: compact ? 14 : 16, lineHeight: 1.2 }}>
          {item?.type === "error"
            ? "❌"
            : item?.type === "warning"
            ? "⚠️"
            : item?.type === "success"
            ? "✅"
            : "🔔"}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: unread ? 800 : 700,
              fontSize: compact ? 13 : 14,
              lineHeight: 1.25,
              wordBreak: "break-word",
            }}
          >
            {item?.title || "Notification"}
          </div>

          <div
            style={{
              opacity: 0.88,
              fontSize: compact ? 12 : 13,
              lineHeight: 1.35,
              marginTop: 2,
              wordBreak: "break-word",
            }}
          >
            {item?.message || ""}
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              fontSize: 11,
              opacity: 0.72,
              marginTop: 4,
            }}
          >
            {!!item?.source && <span>#{item.source}</span>}
            {!!item?.branch && <span>📍 {item.branch}</span>}
            {!!item?.created_at && <span>{fmtWhen(item.created_at)}</span>}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
        {!item?.is_read && (
          <button className="btn tiny" onClick={() => onRead?.(item)}>
            Mark read
          </button>
        )}

        {!!item?.action_url && (
          <a
            className="btn tiny"
            href={item.action_url}
            target={item.action_url.startsWith("http") ? "_blank" : undefined}
            rel={item.action_url.startsWith("http") ? "noreferrer" : undefined}
          >
            Open
          </a>
        )}

        <button className="btn tiny" onClick={() => onDelete?.(item)}>
          Delete
        </button>
      </div>
    </div>
  );
}

export default function NavBar({
  onCreateNewTable,
  onOpenTables,
  onMergeAll,
  onEndOfDay,
}) {
  const navigate = useNavigate();
  const { token, user, isAuthenticated, logout } = useAuth();

  const BASE = (API || "").replace(/\/+$/, "");

  const [liveBranch, setLiveBranchState] = useState(() =>
    safeLower(getActiveBranch() || "")
  );

  const [branchReady, setBranchReady] = useState(false);

  const userEmail = safeLower(user?.email || user?.username || "");
  const allowedBranches = useMemo(
    () => normalizeUserBranches(user?.branches),
    [user?.branches]
  );
  const fallbackBranch = allowedBranches[0] || "";
  const canSwitchBranch = allowedBranches.length > 1;

  useEffect(() => {
    const sync = () => setLiveBranchState(safeLower(getActiveBranch() || ""));
    sync();
    window.addEventListener("bamboo:branch", sync);
    window.addEventListener("app:branch", sync);

    return () => {
      window.removeEventListener("bamboo:branch", sync);
      window.removeEventListener("app:branch", sync);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setBranchReady(false);
      return;
    }

    let active = safeLower(getActiveBranch() || "");
    if (!allowedBranches.includes(active)) active = fallbackBranch;

    if (allowedBranches.length === 1 && active) {
      try {
        setBranch(active, { reload: false, force: true });
      } catch {}
      try {
        lockBranch(active, { reload: false });
      } catch {}
    } else {
      try {
        unlockBranch({ reload: false });
      } catch {}
      if (active) {
        try {
          setBranch(active, { reload: false, force: true });
        } catch {}
      }
    }

    setLiveBranchState(active);
    setBranchReady(Boolean(active));

    try {
      window.dispatchEvent(
        new CustomEvent("bamboo:branchReady", {
          detail: { branch: active, email: userEmail },
        })
      );
    } catch {}
  }, [isAuthenticated, userEmail, allowedBranches.join("|"), fallbackBranch]);

  const handleBranchChange = useCallback(
    (nextRaw) => {
      const next = safeLower(nextRaw);
      if (!next || !allowedBranches.includes(next)) return;

      if (allowedBranches.length > 1) {
        try {
          unlockBranch({ reload: false });
        } catch {}
      }

      try {
        setBranch(next, { reload: false, force: true });
      } catch {}

      if (allowedBranches.length === 1) {
        try {
          lockBranch(next, { reload: false });
        } catch {}
      }

      setLiveBranchState(next);
      setBranchReady(true);

      try {
        window.dispatchEvent(
          new CustomEvent("bamboo:branchReady", {
            detail: { branch: next, email: userEmail },
          })
        );
        window.dispatchEvent(
          new CustomEvent("app:notifications:refresh", {
            detail: { reason: "branch-change", branch: next, ts: Date.now() },
          })
        );
      } catch {}
    },
    [allowedBranches.join("|"), userEmail]
  );

  const waitBranchReady = useCallback(async () => {
    const active = safeLower(getActiveBranch() || "");
    if (active && allowedBranches.includes(active)) return active;

    if (fallbackBranch) {
      handleBranchChange(fallbackBranch);
      return fallbackBranch;
    }

    return "";
  }, [allowedBranches.join("|"), fallbackBranch, handleBranchChange]);

  const getAuthHeaders = () => {
    const h = { "Content-Type": "application/json" };
    if (token) h.Authorization = `Bearer ${token}`;
    return h;
  };

  const apiFetch = useCallback(
    async (path, init = {}) => {
      const url = `${BASE}${path}`;
      const csrf = getCookie("csrf_token") || getCookie("csrf") || "";

      const method = String(init.method || (init.body ? "POST" : "GET")).toUpperCase();
      const headers = { ...(init.headers || {}) };

      if (init.body && !(init.body instanceof FormData)) {
        headers["Content-Type"] = headers["Content-Type"] || "application/json";
      }

      if (["POST", "PUT", "PATCH", "DELETE"].includes(method) && csrf) {
        headers["x-csrf-token"] = headers["x-csrf-token"] || csrf;
      }

      const b = safeLower(getActiveBranch() || fallbackBranch);
      if (b) {
        if (!headers["x-branch"]) headers["x-branch"] = b;
        if (!headers["X-Branch"]) headers["X-Branch"] = b;
      }

      const res = await fetch(url, {
        mode: "cors",
        credentials: "include",
        ...init,
        method,
        headers,
      });

      const text = await res.text().catch(() => "");
      let data = null;

      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        data = null;
      }

      if (!res.ok) {
        const detail = data?.detail || data?.message || text || "Request failed";
        throw new Error(`${res.status} ${res.statusText} — ${detail}`);
      }

      return data || {};
    },
    [BASE, fallbackBranch]
  );

  const apiFetchWaitBranch = useCallback(
    async (path, init = {}) => {
      await waitBranchReady();
      return apiFetch(path, init);
    },
    [waitBranchReady, apiFetch]
  );

  const mergeBusyRef = useRef(false);
  const mergeAbortRef = useRef(null);
  const [mergeBusy, setMergeBusy] = useState(false);

  const doMergeAll = useCallback(async () => {
    if (mergeBusyRef.current) return;

    mergeBusyRef.current = true;
    setMergeBusy(true);

    try {
      if (mergeAbortRef.current) mergeAbortRef.current.abort();
    } catch {}

    const ac = new AbortController();
    mergeAbortRef.current = ac;

    try {
      let data = null;

      try {
        data = await apiFetchWaitBranch("/merge/tables?archive=0", {
          method: "POST",
          signal: ac.signal,
        });
      } catch (e1) {
        const msg1 = String(e1?.message || e1 || "");
        if (/404|405|not found|method not allowed/i.test(msg1)) {
          data = await apiFetchWaitBranch("/merge-tables", {
            method: "POST",
            signal: ac.signal,
          });
        } else {
          throw e1;
        }
      }

      const skipped =
        Array.isArray(data?.skipped) && data.skipped.length
          ? `\n\nSkipped:\n- ${data.skipped.join("\n- ")}`
          : "";

      alert(`${data?.message || "Merged."}${skipped}`);
    } catch (err) {
      const msg = String(err?.message || err || "");
      if (!/aborted|abort/i.test(msg)) {
        alert("❌ Merge failed:\n" + msg);
      }
    } finally {
      mergeBusyRef.current = false;
      setMergeBusy(false);
    }
  }, [apiFetchWaitBranch]);

  useEffect(() => {
    return () => {
      try {
        if (mergeAbortRef.current) mergeAbortRef.current.abort();
      } catch {}

      try {
        if (exportTimerRef.current) window.clearInterval(exportTimerRef.current);
      } catch {}
    };
  }, []);

  const [apiHealth, setApiHealth] = useState(null);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        await apiFetchWaitBranch("/health", { method: "GET" });
        if (alive) setApiHealth(true);
      } catch {
        if (alive) setApiHealth(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [apiFetchWaitBranch, liveBranch]);

  const applyApiChoice = (value) => {
    if (!value) return;

    const u = new URL(window.location.href);

    if (value === "reset") {
      u.searchParams.delete("apiBase");
      setApiBase("reset");
    } else {
      u.searchParams.set("apiBase", value);
      setApiBase(value);
    }

    u.searchParams.delete("city");
    window.location.href = u.toString();
  };

  const onPickApi = (e) => applyApiChoice(e.target.value);

  const currentApiLabel = useMemo(() => {
    if (/^https:\/\/api\.bamboome\.me\/?$/i.test(BASE)) return "global";
    if (/^http:\/\/127\.0\.0\.1:8000\/?$/i.test(BASE)) return "local";

    try {
      const u = new URL(BASE);
      return u.hostname;
    } catch {
      return BASE;
    }
  }, [BASE]);

  const activeBranchLabel = useMemo(() => {
    const b = safeLower(getActiveBranch() || fallbackBranch);
    return BRANCH_LABEL[b] || b || "Not selected";
  }, [liveBranch, fallbackBranch]);

  const [openMenu, setOpenMenu] = useState(null);
  const actionsRef = useRef(null);
  const navRef = useRef(null);
  const endRef = useRef(null);
  const optRef = useRef(null);
  const ckRef = useRef(null);
  const notifRef = useRef(null);

  const [fileDlg, setFileDlg] = useState(false);
  const [openingFolder, setOpeningFolder] = useState(false);

  const [batchOpen, setBatchOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [showUsersDlg, setShowUsersDlg] = useState(false);
  const [showPrivateMediaDlg, setShowPrivateMediaDlg] = useState(false);

  const exportTimerRef = useRef(null);
  const vcfOpenInputRef = useRef(null);
  const [vcfOpenBusy, setVcfOpenBusy] = useState(false);
  const [vcfExportOpen, setVcfExportOpen] = useState(false);
  const [vcfExportBusy, setVcfExportBusy] = useState(false);
  const [vcfExportPercent, setVcfExportPercent] = useState(0);
  const [vcfExportMessage, setVcfExportMessage] = useState("Preparing export...");
  const [vcfExportStatus, setVcfExportStatus] = useState("idle");

  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const [mobileNotifOpen, setMobileNotifOpen] = useState(false);

  const toggleMenu = (key) => {
    setMobileMoreOpen(false);
    setMobileNotifOpen(false);
    setOpenMenu((m) => (m === key ? null : key));
  };

  const closeAll = useCallback(() => {
    setOpenMenu(null);
    setMobileMoreOpen(false);
    setMobileNotifOpen(false);
  }, []);

  useEffect(() => {
    const onDocPointerDown = (e) => {
      if (
        openMenu &&
        !actionsRef.current?.contains(e.target) &&
        !navRef.current?.contains(e.target) &&
        !endRef.current?.contains(e.target) &&
        !optRef.current?.contains(e.target) &&
        !ckRef.current?.contains(e.target) &&
        !notifRef.current?.contains(e.target)
      ) {
        setOpenMenu(null);
      }
    };

    document.addEventListener("pointerdown", onDocPointerDown, { passive: true });
    return () => document.removeEventListener("pointerdown", onDocPointerDown);
  }, [openMenu]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") closeAll();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeAll]);

  const isAdmin =
    Array.isArray(user?.roles) &&
    user.roles.map(String).map((s) => s.toLowerCase()).includes("admin");

  const isAdminAuthed = Boolean(isAuthenticated && isAdmin);

  const handleLogout = async () => {
    try {
      // AuthContext owns the single authoritative logout request.
      await logout();
    } finally {
      try {
        unlockBranch({ reload: false });
      } catch {}

      try {
        setBranch("", { reload: false, force: true });
      } catch {}

      setLiveBranchState("");
      setBranchReady(false);
      closeAll();
      navigate("/login");
    }
  };

  const openBackendFolderOnServer = async () => {
    setOpeningFolder(true);
    try {
      await apiFetchWaitBranch("/backend/open-folder", { method: "POST" });
      alert("✅ Folder opened on the SERVER machine.");
    } catch (e) {
      alert("❌ Could not open folder:\n" + (e?.message || String(e)));
    } finally {
      setOpeningFolder(false);
    }
  };

  const go = (path) => {
    closeAll();
    navigate(path);
  };

  const stopExportTimer = useCallback(() => {
    try {
      if (exportTimerRef.current) {
        window.clearInterval(exportTimerRef.current);
        exportTimerRef.current = null;
      }
    } catch {}
  }, []);

  const resetExportLoading = useCallback(() => {
    stopExportTimer();
    setVcfExportOpen(false);
    setVcfExportBusy(false);
    setVcfExportPercent(0);
    setVcfExportMessage("Preparing export...");
    setVcfExportStatus("idle");
  }, [stopExportTimer]);

  const startExportLoading = useCallback(() => {
    stopExportTimer();

    setVcfExportOpen(true);
    setVcfExportBusy(true);
    setVcfExportStatus("loading");
    setVcfExportPercent(8);
    setVcfExportMessage("Preparing contacts database...");

    exportTimerRef.current = window.setInterval(() => {
      setVcfExportPercent((p) => {
        if (p < 35) return p + 4;
        if (p < 70) return p + 2;
        if (p < 92) return p + 1;
        return p;
      });

      setVcfExportMessage((msg) => {
        if (msg === "Preparing contacts database...") return "Reading contacts...";
        if (msg === "Reading contacts...") return "Building VCF file...";
        if (msg === "Building VCF file...") return "Almost ready...";
        return msg;
      });
    }, 420);
  }, [stopExportTimer]);

  const getDownloadFilename = (response, fallback) => {
    try {
      const cd = response.headers.get("content-disposition") || "";
      const utf8 = cd.match(/filename\*=UTF-8''([^;]+)/i);
      if (utf8?.[1]) return decodeURIComponent(utf8[1].replace(/["]/g, ""));

      const normal = cd.match(/filename="?([^"]+)"?/i);
      if (normal?.[1]) return normal[1].trim();
    } catch {}

    return fallback;
  };

  const openContactsVCFPicker = () => {
    closeAll();

    window.setTimeout(() => {
      try {
        vcfOpenInputRef.current?.click();
      } catch {}
    }, 0);
  };

  const openContactsVCF = async (e) => {
    const file = e?.target?.files?.[0];

    if (!file) return;

    const fileName = String(file.name || "contacts.vcf");
    const looksLikeVcf = /\.vcf$/i.test(fileName) || /vcard/i.test(String(file.type || ""));

    if (!looksLikeVcf) {
      alert("❌ Please choose a valid .vcf file.");
      try {
        e.target.value = "";
      } catch {}
      return;
    }

    setVcfOpenBusy(true);

    try {
      await waitBranchReady();

      const branch = safeLower(getActiveBranch() || fallbackBranch || "contacts");
      const csrf = getCookie("csrf_token") || getCookie("csrf") || "";

      const formData = new FormData();
      formData.append("file", file, fileName);
      formData.append("branch", branch);

      const headers = {
        "x-branch": branch,
        "X-Branch": branch,
      };

      if (token) headers.Authorization = `Bearer ${token}`;
      if (csrf) headers["x-csrf-token"] = csrf;

      const response = await fetch(`${BASE}/contacts/import-vcf`, {
        method: "POST",
        credentials: "include",
        headers,
        body: formData,
      });

      const text = await response.text().catch(() => "");
      let data = null;

      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        data = null;
      }

      if (!response.ok) {
        const detail = data?.detail || data?.message || text || `Import failed (${response.status})`;
        throw new Error(detail);
      }

      const imported = Number(data?.imported ?? data?.created ?? data?.inserted ?? 0);
      const skipped = Number(data?.skipped ?? 0);
      const duplicates = Number(data?.duplicates ?? 0);
      const total = Number(data?.contacts_found ?? data?.total ?? imported + skipped + duplicates);

      alert(
        `✅ VCF opened and imported successfully.\n\nFile: ${fileName}\nBranch: ${branch}\nContacts found: ${total}\nImported: ${imported}\nSkipped: ${skipped}\nDuplicates: ${duplicates}`
      );

      try {
        window.dispatchEvent(
          new CustomEvent("bamboo:contactsImported", {
            detail: { fileName, branch, total, imported, skipped, duplicates },
          })
        );
      } catch {}
    } catch (err) {
      alert("❌ Could not open/import VCF:\n" + (err?.message || String(err)));
    } finally {
      setVcfOpenBusy(false);
      try {
        e.target.value = "";
      } catch {}
    }
  };

  const exportContactsVCF = async () => {
    closeAll();

    if (vcfExportBusy) return;

    startExportLoading();

    try {
      await waitBranchReady();

      const branch = safeLower(getActiveBranch() || fallbackBranch || "export");
      const headers = {
        "x-branch": branch,
        "X-Branch": branch,
      };

      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(`${BASE}/contacts/export-vcf`, {
        method: "GET",
        credentials: "include",
        headers,
      });

      if (!response.ok) {
        let detail = "";

        try {
          const data = await response.json();
          detail = data?.detail || data?.message || "";
        } catch {
          detail = await response.text().catch(() => "");
        }

        throw new Error(detail || `Export failed (${response.status})`);
      }

      setVcfExportMessage("Downloading VCF file...");
      setVcfExportPercent(96);

      const blob = await response.blob();

      if (!blob || blob.size === 0) {
        throw new Error("The backend returned an empty VCF file.");
      }

      const fileName = getDownloadFilename(response, `contacts_${branch}.vcf`);
      const exportedContacts = response.headers.get("x-exported-contacts") || "";
      const skippedContacts = response.headers.get("x-skipped-contacts") || "";
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.style.display = "none";

      document.body.appendChild(a);
      a.click();

      a.remove();
      window.URL.revokeObjectURL(url);

      stopExportTimer();
      setVcfExportStatus("success");
      setVcfExportPercent(100);
      setVcfExportMessage(
        `Export completed: ${fileName}${
          exportedContacts ? ` — ${exportedContacts} contacts` : ""
        }${skippedContacts ? `, ${skippedContacts} skipped` : ""}`
      );

      window.setTimeout(() => {
        resetExportLoading();
      }, 1100);
    } catch (e) {
      stopExportTimer();
      setVcfExportBusy(false);
      setVcfExportStatus("error");
      setVcfExportPercent(100);
      setVcfExportMessage(e?.message || String(e) || "Could not export contacts.");
    }
  };


  const [notifItems, setNotifItems] = useState([]);
  const [notifUnread, setNotifUnread] = useState(0);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifError, setNotifError] = useState("");

  const activeBranchForNotif = safeLower(getActiveBranch() || fallbackBranch || "");

  const loadNotifications = useCallback(
    async ({ silent = false } = {}) => {
      if (!isAuthenticated) {
        setNotifItems([]);
        setNotifUnread(0);
        setNotifError("");
        return;
      }

      if (!silent) setNotifLoading(true);
      setNotifError("");

      try {
        const [listRes, unreadRes] = await Promise.all([
          getNotifications({ limit: 30, unreadOnly: false, branch: activeBranchForNotif }),
          getUnreadNotificationCount({ branch: activeBranchForNotif }),
        ]);

        setNotifItems(Array.isArray(listRes?.items) ? listRes.items : []);
        setNotifUnread(Number(unreadRes?.unread || 0));
      } catch (e) {
        setNotifError(String(e?.message || e || "Failed to load notifications"));
      } finally {
        if (!silent) setNotifLoading(false);
      }
    },
    [isAuthenticated, activeBranchForNotif]
  );

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const id = window.setInterval(() => {
      loadNotifications({ silent: true });
    }, 20000);

    return () => window.clearInterval(id);
  }, [isAuthenticated, loadNotifications]);

  useEffect(() => {
    const sync = () => {
      loadNotifications({ silent: true });
    };

    window.addEventListener("bamboo:branch", sync);
    window.addEventListener("app:branch", sync);

    return () => {
      window.removeEventListener("bamboo:branch", sync);
      window.removeEventListener("app:branch", sync);
    };
  }, [loadNotifications]);

  const handleReadNotification = async (item) => {
    try {
      await markNotificationRead(item?.id);
      setNotifItems((prev) =>
        prev.map((x) =>
          x.id === item.id ? { ...x, is_read: true, read_at: new Date().toISOString() } : x
        )
      );
      setNotifUnread((n) => Math.max(0, Number(n || 0) - 1));
    } catch (e) {
      alert("❌ Could not mark notification as read:\n" + (e?.message || String(e)));
    }
  };

  const handleDeleteNotification = async (item) => {
    try {
      await deleteNotification(item?.id);
      setNotifItems((prev) => prev.filter((x) => x.id !== item.id));
      if (!item?.is_read) {
        setNotifUnread((n) => Math.max(0, Number(n || 0) - 1));
      }
    } catch (e) {
      alert("❌ Could not delete notification:\n" + (e?.message || String(e)));
    }
  };

  const handleReadAllNotifications = async () => {
    try {
      await markAllNotificationsRead({ branch: activeBranchForNotif });
      setNotifItems((prev) =>
        prev.map((x) => ({ ...x, is_read: true, read_at: x.read_at || new Date().toISOString() }))
      );
      setNotifUnread(0);
    } catch (e) {
      alert("❌ Could not mark all notifications as read:\n" + (e?.message || String(e)));
    }
  };

  const handleCreateTestNotification = async () => {
    try {
      await createTestNotification({
        title: "Test notification",
        message: `Hello ${user?.username || user?.email || "user"} — branch ${activeBranchForNotif || "default"}`,
        type: "info",
        source: "navbar",
        branch: activeBranchForNotif,
      });
      await loadNotifications();
      setOpenMenu("notifications");
    } catch (e) {
      alert("❌ Could not create test notification:\n" + (e?.message || String(e)));
    }
  };

  const openMobileNotifications = async () => {
    setOpenMenu(null);
    setMobileMoreOpen(false);
    setMobileNotifOpen(true);
    await loadNotifications();
  };

  return (
    <header className="nav nav-root" role="banner" style={{ position: "relative", zIndex: 30 }}>
      <input
        ref={vcfOpenInputRef}
        type="file"
        accept=".vcf,text/vcard,text/x-vcard"
        onChange={openContactsVCF}
        style={{ display: "none" }}
        aria-hidden="true"
        tabIndex={-1}
      />

      <div className="topbar">
        <div className="left">
          <Link to="/" className="brand" title="Home" aria-label="Home" onClick={closeAll}>
            📋 Contact Manager
          </Link>
        </div>

        <div className="right groupbar" style={{ flexWrap: "wrap" }}>
          <div className="group" ref={actionsRef}>
            <button className="btn bigtap" onClick={() => toggleMenu("actions")} title="Actions">
              ⋯ <span className="hide-sm">Actions</span>
            </button>

            {openMenu === "actions" && (
              <div className="popover" role="menu" aria-label="Actions">
                <PermGate perm="create_table">
                  <button
                    className="pop-item"
                    onClick={() => {
                      closeAll();
                      onCreateNewTable?.();
                    }}
                  >
                    ➕ New Table
                  </button>
                </PermGate>

                <PermGate perm="open_saved_tables">
                  <button
                    className="pop-item"
                    onClick={() => {
                      closeAll();
                      onOpenTables?.();
                    }}
                  >
                    📂 Saved Tables
                  </button>
                </PermGate>

                <PermGate perm="merge_all">
                  <button
                    className="pop-item"
                    disabled={mergeBusy}
                    onClick={() => {
                      if (mergeBusy) return;
                      closeAll();
                      if (onMergeAll) onMergeAll();
                      else doMergeAll();
                    }}
                    title={mergeBusy ? "Merging..." : "Merge All"}
                  >
                    {mergeBusy ? "⏳ Merging..." : "🔁 Merge All"}
                  </button>
                </PermGate>
              </div>
            )}
          </div>

          <div className="group" ref={navRef}>
            <button className="btn bigtap" onClick={() => toggleMenu("nav")} title="Navigate">
              ⋯ <span className="hide-sm">Navigate</span>
            </button>

            {openMenu === "nav" && (
              <div className="popover" role="menu" aria-label="Navigate">
                <Link to="/" className="pop-link" onClick={closeAll}>🏠 Home</Link>
                <Link to="/search-date" className="pop-link" onClick={closeAll}>📅 Search by Date</Link>
                <Link to="/birthdays" className="pop-link" onClick={closeAll}>🎂 Birthday Search</Link>
                <Link to="/reservations" className="pop-link" onClick={closeAll}>📆 Reservations</Link>
                <Link to="/guest-search" className="pop-link" onClick={closeAll}>🔍 Search Guest</Link>
                <Link to="/control" className="pop-link" onClick={closeAll}>⚙ Control Panel</Link>
                <Link to="/competitions" className="pop-link" onClick={closeAll}>🏆 Competitions</Link>
              </div>
            )}
          </div>

          <div className="group" ref={endRef}>
            <button className="btn bigtap" onClick={() => toggleMenu("end")} title="End">
              ⋯ <span className="hide-sm">End</span>
            </button>

            {openMenu === "end" && (
              <div className="popover" role="menu" aria-label="End">
                <PermGate perm="end_of_day">
                  <button
                    className="pop-item danger"
                    onClick={() => {
                      closeAll();
                      onEndOfDay?.();
                    }}
                  >
                    🌙 End of Day
                  </button>
                </PermGate>
              </div>
            )}
          </div>

          {isAuthenticated && (
            <div className="group" ref={notifRef}>
              <button
                className="btn bigtap"
                onClick={() => toggleMenu("notifications")}
                title="Notifications"
                aria-label={`Notifications${notifUnread ? ` (${notifUnread} unread)` : ""}`}
                style={{ position: "relative" }}
              >
                🔔 <span className="hide-sm">Notifications</span>
                {notifUnread > 0 && (
                  <span
                    style={{
                      position: "absolute",
                      top: -6,
                      right: -4,
                      minWidth: 18,
                      height: 18,
                      padding: "0 5px",
                      borderRadius: 999,
                      background: "#ef4444",
                      color: "#fff",
                      fontSize: 11,
                      fontWeight: 800,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      lineHeight: 1,
                      border: "2px solid rgba(15,23,42,.95)",
                    }}
                  >
                    {notifUnread > 99 ? "99+" : notifUnread}
                  </span>
                )}
              </button>

              {openMenu === "notifications" && (
                <div
                  className="popover"
                  role="menu"
                  aria-label="Notifications"
                  style={{
                    width: "min(420px, 92vw)",
                    maxHeight: "70vh",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      paddingBottom: 4,
                      borderBottom: "1px solid rgba(255,255,255,.08)",
                    }}
                  >
                    <div style={{ fontWeight: 800 }}>🔔 Notifications</div>
                    <div style={{ marginLeft: "auto", display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <button className="btn tiny" onClick={() => loadNotifications()}>Refresh</button>
                      <button className="btn tiny" onClick={handleReadAllNotifications}>Read all</button>
                      {isAdminAuthed && (
                        <button className="btn tiny" onClick={handleCreateTestNotification}>Test</button>
                      )}
                    </div>
                  </div>

                  {notifLoading ? (
                    <div style={{ padding: "10px 4px", opacity: 0.8 }}>Loading notifications…</div>
                  ) : notifError ? (
                    <div style={{ padding: "10px 4px", color: "#fca5a5" }}>{notifError}</div>
                  ) : notifItems.length === 0 ? (
                    <div style={{ padding: "10px 4px", opacity: 0.75 }}>No notifications.</div>
                  ) : (
                    <div style={{ display: "grid", gap: 8, overflowY: "auto", paddingRight: 2 }}>
                      {notifItems.map((item) => (
                        <NotificationItem
                          key={item.id}
                          item={item}
                          onRead={handleReadNotification}
                          onDelete={handleDeleteNotification}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {isAdminAuthed && (
            <>
              <div className="group" ref={optRef}>
                <button className="btn bigtap" onClick={() => toggleMenu("options")} title="Options">
                  ⋯ <span className="hide-sm">Options</span>
                </button>

                {openMenu === "options" && (
                  <div className="popover" role="menu" aria-label="Options">
                    <button
                      className="pop-item"
                      onClick={openContactsVCFPicker}
                      disabled={vcfOpenBusy}
                      title="Open/import a .vcf file from Android, iPhone, or desktop into contacts.db"
                    >
                      {vcfOpenBusy ? "⏳ Opening VCF..." : "📂 Open VCF"}
                    </button>

                    <button className="pop-item" onClick={exportContactsVCF} disabled={vcfExportBusy}>
                      {vcfExportBusy ? "⏳ Exporting VCF..." : "📇 Export Contacts VCF"}
                    </button>

                    <button
                      className="pop-item"
                      onClick={() => go("/chat-seen")}
                      title="Upload WhatsApp chat export, detect phone numbers, match contacts.db, and save chat dates"
                    >
                      👁 Chat Seen Contacts
                    </button>

                    <button
                      className="pop-item"
                      onClick={() => {
                        closeAll();
                        setBulkDeleteOpen(true);
                      }}
                    >
                      🗑 Bulk Delete
                    </button>

                    <button
                      className="pop-item"
                      onClick={() => {
                        closeAll();
                        setFileDlg(true);
                      }}
                    >
                      📁 Backend Folder
                    </button>

                    <button
                      className="pop-item"
                      onClick={() => {
                        closeAll();
                        setShowPrivateMediaDlg(true);
                      }}
                    >
                      🖼 Private Media
                    </button>

                    <button
                      className="pop-item"
                      onClick={() => {
                        closeAll();
                        setShowUsersDlg(true);
                      }}
                    >
                      👥 Users
                    </button>
                  </div>
                )}
              </div>

              <div className="group" ref={ckRef}>
                <button className="btn bigtap" onClick={() => toggleMenu("ck")} title="Central Kitchen">
                  🧑‍🍳 <span className="hide-sm">Central&nbsp;Kitchen</span>
                </button>

                {openMenu === "ck" && (
                  <div className="popover" role="menu" aria-label="Central Kitchen">
                    <button
                      className="pop-item"
                      onClick={() => {
                        closeAll();
                        setBatchOpen(true);
                      }}
                    >
                      🧪 CK Batch / Recipe Manager
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

          <div
            className="group"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            <span
              role="button"
              onClick={() => window.open(`${BASE}/health`, "_blank")}
              title={`API: ${BASE}\nBranch: ${safeLower(getActiveBranch() || "") || "(auto)"}\nEmail: ${
                userEmail || "(none)"
              }\nBranch Ready: ${branchReady ? "yes" : "no"}\nClick to open /health`}
              style={{
                padding: "4px 8px",
                borderRadius: 999,
                background:
                  apiHealth === true
                    ? "rgba(0,200,0,.15)"
                    : apiHealth === false
                    ? "rgba(200,0,0,.15)"
                    : "var(--line, rgba(255,255,255,.08))",
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              {apiHealth === true ? "✅ API OK" : apiHealth === false ? "⚠ API OFF" : "… API"}
            </span>

            <div className="hide-sm" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
              <span style={{ opacity: 0.8 }}>Branch:</span>
              {canSwitchBranch ? (
                <select
                  value={liveBranch || fallbackBranch}
                  onChange={(e) => handleBranchChange(e.target.value)}
                  title="Switch active branch for all branch-aware requests"
                >
                  {allowedBranches.map((b) => (
                    <option key={b} value={b}>{BRANCH_LABEL[b] || b}</option>
                  ))}
                </select>
              ) : (
                <span
                  title="This account is assigned to one branch"
                  style={{
                    padding: "4px 10px",
                    borderRadius: 999,
                    border: "1px solid rgba(255,255,255,.12)",
                    background: "rgba(255,255,255,.06)",
                    fontSize: 12,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    whiteSpace: "nowrap",
                  }}
                >
                  🔒 {activeBranchLabel}
                </span>
              )}
            </div>

            <label className="hide-sm" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
              <span style={{ opacity: 0.8 }}>API:</span>
              <select onChange={onPickApi} value="" title={`Current: ${currentApiLabel} → ${BASE}`}>
                <option value="" disabled>{currentApiLabel}</option>
                <option value="https://api.bamboome.me">global</option>
                <option value="http://127.0.0.1:8000">local</option>
                <option value="reset">auto (reset)</option>
              </select>
            </label>

            {isAuthenticated ? (
              <div className="user-chip" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span
                  title="Signed in user"
                  style={{
                    padding: "4px 8px",
                    borderRadius: 999,
                    background: "var(--line, rgba(255,255,255,.08))",
                    fontSize: 13,
                  }}
                >
                  👤{" "}
                  {user?.first_name || user?.last_name
                    ? `${user?.first_name || ""} ${user?.last_name || ""}`.trim()
                    : user?.name || user?.username || user?.sub || "User"}
                </span>

                <button className="btn" title="Log out" onClick={handleLogout} aria-label="Log out">
                  🚪 Log out
                </button>
              </div>
            ) : (
              <button className="btn" title="Sign in" onClick={() => navigate("/login")} aria-label="Sign in">
                🔐 Sign in
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="bottombar" aria-label="Mobile navigation">
        {isAuthenticated && (
          <button
            className="btn bigtap bbtn"
            onClick={openMobileNotifications}
            aria-label={`Notifications${notifUnread ? ` (${notifUnread} unread)` : ""}`}
            title="Notifications"
            style={{ position: "relative" }}
          >
            🔔 <span className="lbl">Notifications</span>
            {notifUnread > 0 && (
              <span
                style={{
                  position: "absolute",
                  top: -6,
                  right: -4,
                  minWidth: 18,
                  height: 18,
                  padding: "0 5px",
                  borderRadius: 999,
                  background: "#ef4444",
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 800,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 1,
                  border: "2px solid rgba(15,23,42,.95)",
                }}
              >
                {notifUnread > 99 ? "99+" : notifUnread}
              </span>
            )}
          </button>
        )}

        <button
          className="btn bigtap bbtn"
          onClick={() => {
            setOpenMenu(null);
            setMobileNotifOpen(false);
            setMobileMoreOpen((v) => !v);
          }}
          aria-label="More"
          title="More"
        >
          ⋯ <span className="lbl">More</span>
        </button>
      </div>

      {mobileNotifOpen && (
        <div className="dialog-overlay" onClick={() => setMobileNotifOpen(false)} style={{ zIndex: 1200 }}>
          <div
            className="dialog-card"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(96vw, 560px)",
              maxWidth: "96vw",
              maxHeight: "84vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div className="dialog-head" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <div className="dialog-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span>🔔 Notifications</span>
                {notifUnread > 0 && (
                  <span
                    style={{
                      padding: "2px 8px",
                      borderRadius: 999,
                      background: "rgba(239,68,68,.18)",
                      border: "1px solid rgba(239,68,68,.35)",
                      fontSize: 12,
                      fontWeight: 800,
                    }}
                  >
                    {notifUnread}
                  </span>
                )}
              </div>

              <div style={{ marginLeft: "auto", display: "flex", gap: 6, flexWrap: "wrap" }}>
                <button className="btn tiny" onClick={() => loadNotifications()}>Refresh</button>
                <button className="btn tiny" onClick={handleReadAllNotifications}>Mark all</button>
                {isAdminAuthed && (
                  <button className="btn tiny" onClick={handleCreateTestNotification}>Test</button>
                )}
                <button className="btn tiny" onClick={() => setMobileNotifOpen(false)}>Exit</button>
              </div>
            </div>

            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                display: "grid",
                gap: 8,
                marginTop: 8,
                paddingRight: 2,
              }}
            >
              {notifLoading ? (
                <div style={{ padding: "10px 4px", opacity: 0.8 }}>Loading notifications…</div>
              ) : notifError ? (
                <div style={{ padding: "10px 4px", color: "#fca5a5" }}>{notifError}</div>
              ) : notifItems.length === 0 ? (
                <div style={{ padding: "10px 4px", opacity: 0.75 }}>No notifications.</div>
              ) : (
                notifItems.map((item) => (
                  <NotificationItem
                    key={`mobile-${item.id}`}
                    item={item}
                    onRead={handleReadNotification}
                    onDelete={handleDeleteNotification}
                    compact
                  />
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {mobileMoreOpen && (
        <div className="sheet" role="dialog" aria-modal="true" aria-label="More menu">
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <div style={{ fontWeight: 800 }}>More</div>
            <button className="btn tiny" style={{ marginLeft: "auto" }} onClick={() => setMobileMoreOpen(false)}>
              Close
            </button>
          </div>

          <div className="api-row" style={{ marginBottom: 10 }}>
            <div className="api-current">
              {apiHealth === true ? "✅" : apiHealth === false ? "⚠" : "…"} {currentApiLabel} {" · "}
              {canSwitchBranch ? "🔀" : "🔒"} {activeBranchLabel}
            </div>
            {canSwitchBranch && (
              <select
                className="api-select"
                value={liveBranch || fallbackBranch}
                onChange={(e) => handleBranchChange(e.target.value)}
                title="Switch active branch"
              >
                {allowedBranches.map((b) => (
                  <option key={b} value={b}>{BRANCH_LABEL[b] || b}</option>
                ))}
              </select>
            )}
            <select className="api-select" onChange={onPickApi} value="" title={`Current: ${currentApiLabel} → ${BASE}`}>
              <option value="" disabled>{currentApiLabel}</option>
              <option value="https://api.bamboome.me">global</option>
              <option value="http://127.0.0.1:8000">local</option>
              <option value="reset">auto (reset)</option>
            </select>
          </div>

          <div className="sheet-sec">
            <div className="sheet-title">Actions</div>

            <PermGate perm="create_table">
              <button
                className="sheet-item"
                onClick={() => {
                  closeAll();
                  onCreateNewTable?.();
                }}
              >
                ➕ New Table
              </button>
            </PermGate>

            <PermGate perm="open_saved_tables">
              <button
                className="sheet-item"
                onClick={() => {
                  closeAll();
                  onOpenTables?.();
                }}
              >
                📂 Saved Tables
              </button>
            </PermGate>

            <PermGate perm="merge_all">
              <button
                className="sheet-item"
                disabled={mergeBusy}
                onClick={() => {
                  if (mergeBusy) return;
                  closeAll();
                  if (onMergeAll) onMergeAll();
                  else doMergeAll();
                }}
                title={mergeBusy ? "Merging..." : "Merge All"}
              >
                {mergeBusy ? "⏳ Merging..." : "🔁 Merge All"}
              </button>
            </PermGate>

            {isAdminAuthed && (
              <>
                <button
                  className="sheet-item"
                  onClick={openContactsVCFPicker}
                  disabled={vcfOpenBusy}
                  title="Open/import a .vcf file from Android, iPhone, or desktop into contacts.db"
                >
                  {vcfOpenBusy ? "⏳ Opening VCF..." : "📂 Open VCF"}
                </button>

                <button className="sheet-item" onClick={exportContactsVCF} disabled={vcfExportBusy}>
                  {vcfExportBusy ? "⏳ Exporting VCF..." : "📇 Export Contacts VCF"}
                </button>

                <button
                  className="sheet-item"
                  onClick={() => go("/chat-seen")}
                  title="Upload WhatsApp chat export, detect phone numbers, match contacts.db, and save chat dates"
                >
                  👁 Chat Seen Contacts
                </button>

                <button
                  className="sheet-item"
                  onClick={() => {
                    closeAll();
                    setBatchOpen(true);
                  }}
                >
                  🧪 CK Batch / Recipe Manager
                </button>

                <button
                  className="sheet-item"
                  onClick={() => {
                    closeAll();
                    setBulkDeleteOpen(true);
                  }}
                >
                  🗑 Bulk Delete
                </button>

                <button
                  className="sheet-item"
                  onClick={() => {
                    closeAll();
                    setFileDlg(true);
                  }}
                >
                  📁 Backend Folder
                </button>

                <button
                  className="sheet-item"
                  onClick={() => {
                    closeAll();
                    setShowPrivateMediaDlg(true);
                  }}
                >
                  🖼 Private Media
                </button>

                <button
                  className="sheet-item"
                  onClick={() => {
                    closeAll();
                    setShowUsersDlg(true);
                  }}
                >
                  👥 Users
                </button>
              </>
            )}
          </div>

          <div className="sheet-sep" />

          <div className="sheet-sec">
            <div className="sheet-title">Navigate</div>
            <button className="sheet-item" onClick={() => go("/")}>🏠 Home</button>
            <button className="sheet-item" onClick={() => go("/search-date")}>📅 Search by Date</button>
            <button className="sheet-item" onClick={() => go("/birthdays")}>🎂 Birthday Search</button>
            <button className="sheet-item" onClick={() => go("/reservations")}>📆 Reservations</button>
            <button className="sheet-item" onClick={() => go("/guest-search")}>🔍 Search Guest</button>
            <button className="sheet-item" onClick={() => go("/control")}>⚙ Control Panel</button>
            <button className="sheet-item" onClick={() => go("/competitions")}>🏆 Competitions</button>
          </div>

          <div className="sheet-sep" />

          <div className="sheet-sec">
            <div className="sheet-title">End</div>
            <PermGate perm="end_of_day">
              <button
                className="sheet-item danger"
                onClick={() => {
                  closeAll();
                  onEndOfDay?.();
                }}
              >
                🌙 End of Day
              </button>
            </PermGate>
          </div>

          <div className="sheet-sep" />

          <div className="sheet-sec">
            <div className="sheet-title">Account</div>
            {isAuthenticated ? (
              <button className="sheet-item danger" onClick={handleLogout}>
                🚪 Log out
              </button>
            ) : (
              <button
                className="sheet-item"
                onClick={() => {
                  closeAll();
                  navigate("/login");
                }}
              >
                🔐 Sign in
              </button>
            )}
          </div>
        </div>
      )}

      {vcfExportOpen && (
        <div
          className="dialog-overlay"
          onClick={() => {
            if (!vcfExportBusy) resetExportLoading();
          }}
          style={{
            zIndex: 2500,
            padding: 16,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            className="dialog-card"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(440px, 96vw)",
              maxWidth: "96vw",
              borderRadius: 22,
              overflow: "hidden",
              boxShadow: "0 35px 100px rgba(0,0,0,.45)",
            }}
          >
            <div style={{ display: "grid", gap: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                  aria-hidden="true"
                  style={{
                    width: 46,
                    height: 46,
                    borderRadius: 16,
                    display: "grid",
                    placeItems: "center",
                    background:
                      vcfExportStatus === "error"
                        ? "rgba(239,68,68,.16)"
                        : vcfExportStatus === "success"
                        ? "rgba(34,197,94,.16)"
                        : "rgba(14,165,233,.16)",
                    border: "1px solid rgba(255,255,255,.12)",
                    fontSize: 24,
                  }}
                >
                  {vcfExportStatus === "error" ? "❌" : vcfExportStatus === "success" ? "✅" : "📇"}
                </div>

                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 18, fontWeight: 900, lineHeight: 1.2 }}>
                    Export Contacts VCF
                  </div>
                  <div style={{ fontSize: 13, opacity: 0.76, marginTop: 3 }}>
                    Works on desktop, Android, iPhone, tablet, and all modern browsers.
                  </div>
                </div>
              </div>

              <div
                style={{
                  fontSize: 14,
                  lineHeight: 1.45,
                  color:
                    vcfExportStatus === "error"
                      ? "#fecaca"
                      : vcfExportStatus === "success"
                      ? "#bbf7d0"
                      : "inherit",
                  wordBreak: "break-word",
                }}
              >
                {vcfExportMessage}
              </div>

              <div
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={vcfExportPercent}
                style={{
                  height: 12,
                  borderRadius: 999,
                  background: "rgba(148,163,184,.22)",
                  overflow: "hidden",
                  border: "1px solid rgba(255,255,255,.08)",
                }}
              >
                <div
                  style={{
                    width: `${Math.max(0, Math.min(100, vcfExportPercent))}%`,
                    height: "100%",
                    borderRadius: 999,
                    background:
                      vcfExportStatus === "error"
                        ? "#ef4444"
                        : vcfExportStatus === "success"
                        ? "#22c55e"
                        : "linear-gradient(90deg, #22c55e, #06b6d4)",
                    transition: "width .25s ease",
                  }}
                />
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ fontSize: 12, opacity: 0.72 }}>
                  {Math.round(vcfExportPercent)}%
                </div>

                {vcfExportStatus === "error" && (
                  <button className="btn tiny" style={{ marginLeft: "auto" }} onClick={resetExportLoading}>
                    Close
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <UserAdminModal
        open={showUsersDlg}
        onClose={() => setShowUsersDlg(false)}
        apiFetch={apiFetchWaitBranch}
      />

      <PrivateMediaManager
        open={showPrivateMediaDlg}
        onClose={() => setShowPrivateMediaDlg(false)}
        apiFetch={apiFetchWaitBranch}
        isAdmin={isAdminAuthed}
      />

      {fileDlg && (
        <div className="dialog-overlay" onClick={() => setFileDlg(false)}>
          <div
            className="dialog-card"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: 980,
              width: "95vw",
              height: "80vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div className="dialog-head" style={{ gap: 8 }}>
              <div className="dialog-title">📁 Backend Folder</div>
              <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                <button className="btn" onClick={openBackendFolderOnServer} disabled={openingFolder}>
                  {openingFolder ? "Opening…" : "Open on this PC"}
                </button>
                <a className="btn" href={`${BASE}/backend/browse`} target="_blank" rel="noreferrer">
                  Open in new tab
                </a>
                <button className="btn tiny" onClick={() => setFileDlg(false)}>
                  Close
                </button>
              </div>
            </div>

            <div
              style={{
                flex: 1,
                border: "1px solid rgba(255,255,255,.1)",
                borderRadius: 8,
                overflow: "hidden",
              }}
            >
              <iframe
                title="Backend Browser"
                src={`${BASE}/backend/browse`}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  background: "#0f172a",
                }}
              />
            </div>
          </div>
        </div>
      )}

      {batchOpen && (
        <div className="dialog-overlay" onClick={() => setBatchOpen(false)}>
          <div
            className="dialog-card"
            style={{
              maxWidth: "980px",
              width: "96vw",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="dialog-head" style={{ alignItems: "center", gap: 8 }}>
              <div className="dialog-title">🧑‍🍳 CK Batch / Recipe Manager</div>
              <button className="btn tiny" onClick={() => setBatchOpen(false)}>
                Close
              </button>
            </div>

            <div style={{ flex: 1, minHeight: 0, overflow: "auto", marginTop: 8 }}>
              <BatchManager />
            </div>
          </div>
        </div>
      )}

      {bulkDeleteOpen && (
        <BulkDeleteModal
          open={bulkDeleteOpen}
          onClose={() => setBulkDeleteOpen(false)}
          apiBase={BASE}
          authHeaders={getAuthHeaders()}
        />
      )}
    </header>
  );
}